// ── Delivery Aggregator Integration Types ────────────────────────────────────

export type DeliverySource = 'talabat' | 'deliveroo' | 'careem' | 'noon' | 'hungerstation' | 'keeta' | 'zomato' | 'swiggy' | 'deliverect' | 'native' | 'other';

export type DeliveryStatus =
  | 'pending_acceptance'
  | 'accepted'
  | 'rejected'
  | 'preparing'
  | 'ready_for_pickup'
  | 'rider_assigned'
  | 'picked_up'
  | 'delivered'
  | 'cancelled'
  | 'failed';

// ── Delivery Address ────────────────────────────────────────────────────────

export interface DeliveryAddress {
  line1: string;
  line2?: string;
  city?: string;
  area?: string;
  lat?: number;
  lng?: number;
}

// ── Delivery Platform Config (per venue per platform) ───────────────────────

export interface DeliveryPlatformConfig {
  id: string;
  restaurant_id: string;
  platform: DeliverySource;
  is_enabled: boolean;
  is_paused: boolean;
  paused_until: string | null;
  auto_accept: boolean;
  default_prep_minutes: number;
  store_id: string | null;
  webhook_secret: string | null;
  commission_rate: number | null;
  menu_sync_enabled: boolean;
  last_menu_sync_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// ── Parsed Delivery Order (normalized from aggregator webhook) ──────────────

export interface ParsedDeliveryOrderItem {
  externalItemId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  notes?: string;
  addons?: Array<{ name: string; price: number; quantity: number }>;
}

export interface ParsedDeliveryOrder {
  externalOrderId: string;
  source: DeliverySource;
  storeId: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  deliveryAddress: DeliveryAddress;
  deliveryInstructions?: string;
  items: ParsedDeliveryOrderItem[];
  subtotal: number;
  deliveryFee: number;
  totalAmount: number;
  currency: string;
  estimatedPrepMinutes?: number;
  placedAt: string;
  metadata?: Record<string, unknown>;
}

// ── Delivery Stats ──────────────────────────────────────────────────────────

export interface DeliveryStats {
  total_orders: number;
  total_revenue: number;
  total_commission: number;
  total_net: number;
  avg_prep_minutes: number;
  orders_by_platform: Record<string, number>;
  revenue_by_platform: Record<string, number>;
}

// ── Delivery Provider Interface ─────────────────────────────────────────────

export interface DeliveryProviderInterface {
  name: string;
  acceptOrder(externalId: string, prepMinutes: number): Promise<void>;
  rejectOrder(externalId: string, reason: string): Promise<void>;
  markReady(externalId: string): Promise<void>;
  updatePrepTime(externalId: string, minutes: number): Promise<void>;
  pauseStore(storeId: string, durationMinutes: number): Promise<void>;
  resumeStore(storeId: string): Promise<void>;
}

// ── WebSocket Payloads ──────────────────────────────────────────────────────

export interface DeliveryOrderReceivedPayload {
  orderId: string;
  orderNumber: number;
  source: DeliverySource;
  customerName: string;
  itemCount: number;
  totalAmount: number;
  estimatedPrepMinutes: number;
  restaurantId: string;
}

export interface DeliveryStatusChangedPayload {
  orderId: string;
  deliveryStatus: DeliveryStatus;
  riderName?: string;
  riderPhone?: string;
  riderEta?: string;
  restaurantId: string;
}

// ── Platform color mapping (for UI badges) ──────────────────────────────────

export const DELIVERY_PLATFORM_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  talabat: { bg: 'bg-orange-500/10', text: 'text-orange-500', label: 'Talabat' },
  deliveroo: { bg: 'bg-teal-500/10', text: 'text-teal-500', label: 'Deliveroo' },
  careem: { bg: 'bg-green-500/10', text: 'text-green-500', label: 'Careem' },
  noon: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', label: 'Noon Food' },
  hungerstation: { bg: 'bg-purple-500/10', text: 'text-purple-500', label: 'HungerStation' },
  keeta: { bg: 'bg-pink-500/10', text: 'text-pink-500', label: 'Keeta' },
  zomato: { bg: 'bg-red-500/10', text: 'text-red-500', label: 'Zomato' },
  swiggy: { bg: 'bg-orange-600/10', text: 'text-orange-600', label: 'Swiggy' },
  deliverect: { bg: 'bg-blue-500/10', text: 'text-blue-500', label: 'Deliverect' },
  native: { bg: 'bg-indigo-500/10', text: 'text-indigo-500', label: 'Native' },
  other: { bg: 'bg-zinc-500/10', text: 'text-zinc-500', label: 'Other' },
};

// ── Delivery Reliability Types ─────────────────────────────────────────────

export type DeliveryEventType = 'order_created' | 'order_updated' | 'order_cancelled' | 'status_changed' | 'rider_assigned' | 'rider_update';
export type DeliveryEventStatus = 'received' | 'processing' | 'processed' | 'failed' | 'dead_letter';

export interface DeliveryOrderEvent {
  id: string;
  restaurantId: string;
  externalOrderId: string;
  platform: string;
  eventType: DeliveryEventType;
  idempotencyKey: string | null;
  status: DeliveryEventStatus;
  errorMessage: string | null;
  retryCount: number;
  maxRetries: number;
  nextRetryAt: string | null;
  processedAt: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface DeliverySettlement {
  id: string;
  restaurantId: string;
  orderId: string | null;
  platform: string;
  externalOrderId: string | null;
  grossAmount: number;
  commission: number;
  deliveryFeeCollected: number;
  platformFee: number;
  taxAmount: number;
  packagingCharges: number;
  netPayout: number;
  settlementStatus: 'pending' | 'confirmed' | 'settled' | 'disputed';
  settlementDate: string | null;
  settlementReference: string | null;
  currency: string;
  createdAt: string;
}

/** Valid delivery status transitions (state machine) */
export const DELIVERY_STATE_TRANSITIONS: Record<DeliveryStatus, DeliveryStatus[]> = {
  pending_acceptance: ['accepted', 'rejected', 'cancelled'],
  accepted: ['preparing', 'cancelled'],
  rejected: [],
  preparing: ['ready_for_pickup', 'cancelled'],
  ready_for_pickup: ['rider_assigned', 'picked_up', 'cancelled'],
  rider_assigned: ['picked_up', 'cancelled'],
  picked_up: ['delivered', 'failed'],
  delivered: [],
  cancelled: [],
  failed: [],
};

/** Validate delivery status transition */
export function isValidDeliveryTransition(from: DeliveryStatus, to: DeliveryStatus): boolean {
  return DELIVERY_STATE_TRANSITIONS[from]?.includes(to) ?? false;
}
